/* eslint-disable react/no-danger */
import { useState, useEffect } from 'react';

import parse from 'html-react-parser';
import Head from 'next/head';
import Link from 'next/link';
import Slider from 'react-slick';
import { Nav, NavLink, TabContent, TabPane } from 'reactstrap';

import Layout from '../../components/layout/layout';
import useI18n from '../../hooks/use-i18n';
import { languages, contentLanguageMap } from '../../language.config';
import { embedCodes, cartDisabledMarkets } from '../../links.config';
import getTranslationKeys, { PAGEID } from '../../utils/i18nRosseta';
import END_POINTS, { PAGES_URL } from '../../utils/shopify-endpoints';
import { aboutPageSliderSettings } from '../../utils/slider.config';
import { SHOPIFY_URLS, gumletAWSClean, igFeeds, removeEmbeds } from '../../utils/utils';

import '../../styles/revamp/about.scss';

const About = () => {
  const i18n = useI18n();
  const COUNTRY_LANGUAGE = i18n.activeLocale;
  const aboutUs = i18n.t('about_revamp');
  const footer = i18n.t('footer');
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const pageMetaData = i18n.t('metadata').aboutPage;
  const [journeyTab, setJourneyTab] = useState(1);

  useEffect(() => {
    igFeeds(document, 'script', 'EmbedSocialHashtagScript');

    return () => {
      removeEmbeds(document, 'EmbedSocialHashtagScript', 'embedsocial-hashtag');
    };
  }, [country]);

  return (
    <Layout
      page="about"
      title={pageMetaData.title}
      insiderPage="About"
      description={pageMetaData.description}
      keywords={pageMetaData.keywords}
      analyticData={{ name: 'About', properties: { } }}
      pageUrl="about"
      headerCategory="about"
      pageEnabledMarkets={[]}
    >
      <Head>
        <link rel="stylesheet" href="/vendor/slick/slick.min.css" />
        <link rel="stylesheet" href="/vendor/slick/slick-theme.min.css" defer={true} />
      </Head>
      <main className="about-revamp">
        <section className="about-revamp__banner full-screen-view">
          <div className="banner-video">
            <video
              loop={true}
              muted={true}
              poster="/img/about-revamp/about-banner-poster.jpg"
              playsInline={true}
              autoPlay={true}
              className="d-sm-block d-none"
            >
              <source src="/img/about-revamp/about-banner-video.mp4" type="video/mp4" />
              <source src="/img/about-revamp/about-banner-video.ogg" type="video/ogg" />
            </video>
            <video
              loop={true}
              muted={true}
              poster="/img/about-revamp/about-banner-poster_mobile.png"
              playsInline={true}
              autoPlay={true}
              className="d-sm-none"
            >
              <source src="/img/about-revamp/about-banner-video_mobile.mp4" type="video/mp4" />
              <source src="/img/about-revamp/about-banner-video_mobile.ogg" type="video/ogg" />
            </video>
          </div>
          <div className="cds--grid">
            <h1
              data-lokalise=""
              data-key="about_revamp::banner_title"
              dangerouslySetInnerHTML={{ __html: aboutUs.banner_title }}
            />
          </div>
        </section>
        <section className="about-revamp__whatdo full-screen-view" id="about-whatwedo">
          <div className="cds--grid">
            <p
              data-lokalise=""
              data-key="about_revamp::banner_title"
            >
              {aboutUs.banner_label}
            </p>
            <h2
              data-lokalise=""
              data-key="about_revamp::banner_title"
            >
              {aboutUs.banner_content}
            </h2>
            <div className="iso-container">
              <img src="/img/bsi_white.svg" alt="ISO Logo" data-gumlet="false" />
              <p data-key="footer::iso_description" data-lokalise="">{parse(footer.iso_description)}</p>
            </div>
          </div>
        </section>
        <section className="about-revamp__story" id="about-story">
          <div className="cds--grid content-box">
            <div className="content-box__image">
              <img
                data-src={gumletAWSClean('/img/about-revamp/about-story.png')}
                alt="About | Story"
                data-gumlet="true"
              />
            </div>
            <div className="content-box__text">
              <h3
                data-lokalise=""
                data-key="about_revamp::story_title"
              >
                {aboutUs.story_title}
              </h3>
              <p
                data-lokalise=""
                data-key="about_revamp::story_content"
              >
                {aboutUs.story_content}
              </p>
            </div>
          </div>
        </section>
        <section className="about-revamp__journey" id="about-journey">
          <div className="cds--grid">
            <h3
              data-lokalise=""
              data-key="about_revamp::journey_title"
            >
              {aboutUs.journey_title}
            </h3>
            <Nav tabs={true}>
              {aboutUs.journey_content.data && aboutUs.journey_content.data.length > 0
                && aboutUs.journey_content.data.map((tab, index) => (
                  <>
                    <li className="nav-item">
                      <NavLink
                        className={({ active: journeyTab === (index + 1) })}
                        onClick={() => { setJourneyTab(index + 1); }}
                      >
                        <p
                          data-lokalise=""
                          data-key={`about_revamp::journey_content::${index}::year_number`}
                        >
                          {tab.year_number}
                        </p>
                      </NavLink>
                    </li>
                  </>
                ))}
            </Nav>
            <TabContent activeTab={journeyTab}>
              {aboutUs.journey_content.data && aboutUs.journey_content.data.length > 0
                && aboutUs.journey_content.data.map((tab, index) => (
                  <>
                    <TabPane tabId={index + 1}>
                      <div className="content-box">
                        <div className="content-box__image">
                          <img
                            src={`/img/about-revamp/about-journey${tab.year_number}.png`}
                            alt={`About | Journey ${tab.year_number}`}
                            data-gumlet="false"
                          />
                        </div>
                        <div className="content-box__text">
                          <h4
                            data-lokalise=""
                            data-key={`about_revamp::journey_content::${index}::year_title`}
                          >
                            {tab.year_title}
                          </h4>
                          <p
                            data-lokalise=""
                            data-key={`about_revamp::journey_content::${index}::year_content`}
                            dangerouslySetInnerHTML={{ __html: tab.year_content }}
                          />
                        </div>
                      </div>
                    </TabPane>
                  </>
                ))}
            </TabContent>
          </div>
        </section>
        <section className="about-revamp__investors">
          <div className="content-wrapper">
            <h3
              data-lokalise=""
              data-key="about_revamp::investor_title"
            >
              {aboutUs.investor_title}
            </h3>
            <p
              data-lokalise=""
              data-key="about_revamp::investor_content"
            >
              {aboutUs.investor_content}
            </p>
          </div>
          <Slider {...aboutPageSliderSettings}>
            <img src="/img/about-revamp/about-investor1.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor2.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor3.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor4.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor5.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor6.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor7.png" alt="About | Investor" data-gumlet="false" />
            <img src="/img/about-revamp/about-investor8.png" alt="About | Investor" data-gumlet="false" />
          </Slider>
        </section>
        <section className="about-revamp__partner" id="about-partner">
          <div className="cds--grid content-box">
            <div className="content-box__image">
              <img src="/img/about-revamp/about-partner.jpg" alt="About | Partners" data-gumlet="false" />
            </div>
            <div className="content-box__text">
              <h3
                data-lokalise=""
                data-key="about_revamp::partner_title"
              >
                {aboutUs.partner_title}
              </h3>
              <p
                data-lokalise=""
                data-key="about_revamp::partner_content"
              >
                {aboutUs.partner_content}
              </p>
              <Link href="/[lng]/dentist" as={`/${i18n.activeLocale}/dentist`} prefetch={false}>
                <a
                  role="presentation"
                  className="about-link"
                  data-lokalise=""
                  data-key="about_revamp::partner_link"
                >
                  {aboutUs.partner_link}
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="about-revamp__country">
          <div className="country-banner">
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-japan.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-vietnam.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-malaysia.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-singapore.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-thailand.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-jakarta.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-hongkong.jpg )` }} />
            <span style={{ backgroundImage: `url( /img/about-revamp/about-country-taiwan.jpg )` }} />
          </div>
          <h2
            data-lokalise=""
            data-key="about_revamp::country_title"
          >
            {aboutUs.country_title}
          </h2>
        </section>
        <section className="about-revamp__highlight" id="about-highlight">
          <div className="cds--grid">
            <div className="cds--row">
              <div className="cds--offset-lg-2 cds--col-lg-12">
                <h3
                  data-lokalise=""
                  data-key="about_revamp::highlight_title"
                >
                  {aboutUs.highlight_title}
                </h3>
              </div>
            </div>
            <div className="cds--row justify-content-center content-wrapper">
              {aboutUs.highlight_content.data && aboutUs.highlight_content.data.length > 0
                && aboutUs.highlight_content.data.map((blog, index) => (
                  <>
                    <div className="cds--col-lg-4">
                      <img
                        src={`/img/about-revamp/about-blog-${index + 1}.jpg`}
                        alt="About | Blog"
                        data-gumlet="false"
                      />
                      <h4>{blog.blog_title}</h4>
                      <p>{blog.blog_content}</p>
                      <a href={blog.blog_link}>{aboutUs.read_more}</a>
                    </div>
                  </>
                ))}
            </div>
          </div>
        </section>
        {country !== 'th' && (
        <section className="instafeed-section about-revamp__instafeed" id="about-community">
          <h3
            data-lokalise=""
            data-key="about_revamp::community_title"
          >
            {aboutUs.community_title}
          </h3>
          <div className="cds--grid">
            <div className="cds--row m-0">
              <div className="cds--offset-lg-2 cds--col-lg-12">
                <div className="embedsocial-hashtag" data-ref={embedCodes[country].instagram} />
              </div>
            </div>
          </div>
        </section>
        )}
        {!cartDisabledMarkets.includes(country) && (
        <section className="about-revamp__footer" id="about-join">
          <div className="cds--grid">
            <div className="title-box">
              <h3
                data-lokalise=""
                data-key="about_revamp::footer_title"
              >
                {aboutUs.footer_title}
              </h3>
              <p
                data-lokalise=""
                data-key="about_revamp::footer_content"
              >
                {aboutUs.footer_content}
              </p>
            </div>
            <a
              className="about-link"
              href={`${SHOPIFY_URLS[COUNTRY_LANGUAGE]}${PAGES_URL}${END_POINTS.SHOPALL}`}
              target="_blank"
              data-lokalise=""
              data-key="about_revamp::footer_link"
              referrerPolicy="no-referrer"
              rel="noreferrer"
            >
              {aboutUs.footer_link}
            </a>
            <img src="/img/about-revamp/about-footer.jpg" alt="About | Start journey" data-gumlet="false" />
          </div>
        </section>
        )}
      </main>
    </Layout>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.ABOUT;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}

export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default About;
